import { Component, Injector } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { MuException } from 'src/app/components/shared/types/exception.type';
import { Flags } from 'src/app/components/shared/types/flags.type';
import { MuComponent } from 'src/app/components/shared/types/MuComponent.componetent';
import { MassiveService } from '../../massive.service';
import { UploadFileResponse } from '../../models/uploadFileResponse.model';
import { MassiveCfdiFlags } from './massive-cfdi.flags';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';

@Component({
  selector: 'app-massive-cfdi',
  templateUrl: './massive-cfdi.component.html',
  styleUrls: ['./massive-cfdi.component.sass'],
})
export class MassiveCfdiComponent extends MuComponent {
  formData: UntypedFormGroup;
  flagList: Flags<MassiveCfdiFlags> = new Flags<MassiveCfdiFlags>();
  massiveCfdiFlags = MassiveCfdiFlags;
  errorMessage: string;
  cfdiId: string = '';
  defaultError: string = 'Internal server Error';
  cfdiFileBase64: string;
  cfdiFileName: string;
  cfdiDownloaded: boolean = false;
  downloadFileModalSubTitle: string;

  private formBuilder: UntypedFormBuilder;
  private translateService: TranslateService;

  constructor(private massiveService: MassiveService, private injector: Injector) {
    super();
    this.formBuilder = this.injector.get<UntypedFormBuilder>(UntypedFormBuilder);
    this.translateService = this.injector.get<TranslateService>(TranslateService);
    this.downloadFileModalSubTitle = this.translateService.instant('context.massive.cfdi.fileModal.downloadCFDI');
  }

  muOnInit(): void {
    this.watchFlags();
    this.initData();
  }

  /**
   * init component data
   * @return void
   */
  initData(): void {
    this.cfdiFileBase64 = null;
    this.cfdiFileName = null;
    this.cfdiDownloaded = false;
    this.formData = this.formBuilder.group({
      file: new UntypedFormControl('', []),
    });
  }

  /**
   *  watch flags
   *  @return void
   */
  watchFlags(): void {
    this.watch(this.flagList.valueChanges(this.massiveCfdiFlags.UPLOAD_FILE)).subscribe(async flag => {
      this.uploadFile(this.formData.get('file').value);
      flag.toggle();
    });

    this.watch(this.flagList.valueChanges(this.massiveCfdiFlags.CANCEL_DOWNLOAD_CFDI)).subscribe(async flag => {
      this.hideModals();
      flag.toggle();
    });

    this.watch(this.flagList.valueChanges(this.massiveCfdiFlags.DOWNLOAD_CFDI)).subscribe(async flag => {
      this.getCfdi(this.cfdiFileName);
      flag.toggle();
    });
  }

  /**
   * Get CFDI
   * @param fileName
   */
  async getCfdi(fileName: string): Promise<void> {
    if (this.cfdiFileBase64) {
      try {
        this.cfdiDownloaded = await this.saveAsPdfFile(this.cfdiFileBase64, `${fileName}`);
        this.downloadFileModalSubTitle = this.translateService.instant('context.massive.cfdi.fileModal.succesDownloding');
      } catch (error) {
        this.showErrorModal(error);  
      }
    } else {
      this.showErrorModal(this.translateService.instant('context.massive.cfdi.fileModal.fileGenerationError'));
    }
  }

  /**
   * Upload Loaded file
   * @param file
   * @return void
   */
  uploadFile(file: File): void {
    this.showloadingModal();
    this.watch(this.massiveService.getCfdiFile(file)).subscribe(
      (response: UploadFileResponse) => {
        if (response.status === "FAILED" || response.status === 500) {
          if (response.message) {
            this.showErrorModal(response.message);
          } else if(response.errorMsg){
            this.showErrorModal(response.errorMsg);
          } else {
            this.showErrorModal(this.defaultError);
          }  
        }else{
          this.cfdiFileBase64 = response.data;
          this.cfdiFileName = response.id;
          this.showDownloadFileModal();
        }
      },
      (error) => {
        if(error.message){
          this.showErrorModal(error.message);
        }else{
          this.showErrorModal(error);
        }
      }
    );
  }

  /**
 * Downloads file
 * @param {string} base64String Cadena en base64 para procesar
 * @param {string} fileName Nombre del archivo
 * @return {Promise<boolean>}
 */
  saveAsPdfFile(base64String: string, fileName: string): Promise<boolean> {
    return new Promise(async (resolve, reject) => {
      try {
        // Validate if the string is base64
        if (!base64String) {
          this.showErrorModal(this.translateService.instant('exception.stringCannotBeNull'));
          throw new MuException({
            message: this.translateService.instant('exception.stringCannotBeNull'),
          });
        }
        const regExpValidateBase64 = /^([A-Za-z0-9+/]{4})*([A-Za-z0-9+/]{4}|[A-Za-z0-9+/]{3}=|[A-Za-z0-9+/]{2}==)$/;
        if (!regExpValidateBase64.test(base64String)) {
          this.showErrorModal(this.translateService.instant('exception.stringIsNotBase64'));
          throw new MuException({
            message: this.translateService.instant('exception.stringIsNotBase64'),
          });
        }
        const PDF_TYPE = 'application/pdf';
        const PDF_EXTENSION = '.pdf';
        const byteCharacters = atob(base64String);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const data = new Blob([byteArray], {type: PDF_TYPE});
        saveAs(data, fileName + PDF_EXTENSION);
        resolve(true);
      } catch (error: MuException | any) {
        reject(error);
      }
    });
  }

  /**
   * show loading modal
   * @return void
   */
  showloadingModal(): void {
    this.hideModals();
    this.flagList.enable(MassiveCfdiFlags.SHOW_UPLOAD_FILE_MODAL);
  }

  /**
   * show Download File Modal
   * @return void
   */
  showDownloadFileModal(): void {
    this.hideModals();
    this.flagList.enable(MassiveCfdiFlags.SHOW_DOWNLOAD_FILE_MODAL);
  }

  /**
   * Show error modal with error message
   * @param errorMessage
   * @return void
   */
  showErrorModal(errorMessage: string = this.defaultError): void {
    this.errorMessage = errorMessage;
    this.hideModals();
    this.flagList.enable(MassiveCfdiFlags.SHOW_CFDI_ERROR_MODAL);
  }

  /**
   * Hides the displayed modals from the view.
   * @return void
   */
  hideModals(): void {
    this.flagList.disable(MassiveCfdiFlags.SHOW_UPLOAD_FILE_MODAL);
    this.flagList.disable(MassiveCfdiFlags.SHOW_DOWNLOAD_FILE_MODAL);
    this.flagList.disable(MassiveCfdiFlags.SHOW_CFDI_ERROR_MODAL);
  }

  /**
   * Closes error modal dialog
   * @param fileUploadform
   * @return void
   */
  closeErrorModalDialog(fileUploadform: any): void {
    fileUploadform.fileControl.clear();
    fileUploadform.fileUploaded = false;
    this.initData();
  }

  muOnDestroy(): void {
    /** */
  }
}
